import { createSlice } from '@reduxjs/toolkit';
import { mergeReduxStateData } from 'library/helpers/reduxHelpers';

const initialState = {
	data: {},
	loading: false,
	error: null,
	timeStamp: null,

	// ✅ Complete States
	completeDeleteDevice: false,

	// ✅ Devices Loadings
	devicesLoadingComplete: false,
	createDeviceLoading: false,
	updateDeviceLoading: false,
	deleteDeviceLoading: false,

	// ✅ Devices Fields Loadings
	loadDeviceFieldsLoading: false,
	createDeviceFieldLoading: false,
	updateDeviceFieldLoading: false,
	deleteDeviceFieldLoading: false,

	// ✅ Devices Location Loadings
	loadDeviceLocationLoading: false,
	createDeviceLocationLoading: false,
	updateDeviceLocationLoading: false,
	deleteDeviceLocationLoading: false,

	// ✅ Devices Helpers Loadings
	actionDevicesLoading: false
};

const devicesSlice = createSlice({
	name: 'devices',
	initialState,
	reducers: {
		// ✅ Devices Actions
		loadDevices: (state) => {
			state.loading = true;
			state.error = null;
		},
		loadDevicesSuccess: (state, action) => {
			state.timeStamp = Date.now();
			state.loading = false;

			const { orgID, data } = action.payload;
			state.data[orgID] = mergeReduxStateData(state.data[orgID] || [], data, 'deviceID');
			state.devicesLoadingComplete = true;
		},
		loadDevicesError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
		},
		loadAllDevicesLocations: (state) => {
			state.error = null;
		},
		loadAllDevicesLocationsSuccess: (state, action) => {
			const { orgID, data } = action.payload;

			state.data[orgID] = state.data[orgID]?.map((device) => {
				const allRows = data[device.deviceID] || [];
				const location = allRows.filter((row) => row.floorID !== null);
				const sites = allRows.filter((row) => row.siteID !== null).map((row) => ({ siteID: row.siteID }));
				// Deduplicate sites (a device on a floor also has siteID)
				const uniqueSites = [...new Map(sites.map((s) => [s.siteID, s])).values()];
				return { ...device, location: location.length ? location : null, sites: uniqueSites };
			});
		},
		loadAllDevicesLocationsError: (state, action) => {
			state.loading = false;
			state.error = action.payload;
		},

		createDevice: (state) => {
			state.createDeviceLoading = true;
			state.error = null;
		},
		createDeviceSuccess: (state) => {
			state.createDeviceLoading = false;
		},
		createDeviceError: (state, action) => {
			state.createDeviceLoading = false;
			state.error = action.payload;
		},

		createMultipleDevices: (state) => {
			state.createDeviceLoading = true;
			state.error = null;
		},
		createMultipleDevicesSuccess: (state) => {
			state.createDeviceLoading = false;
		},
		createMultipleDevicesError: (state, action) => {
			state.createDeviceLoading = false;
			state.error = action.payload;
		},

		updateDevice: (state) => {
			state.updateDeviceLoading = true;
		},
		updateDeviceSuccess: (state) => {
			state.updateDeviceLoading = false;
		},
		updateDeviceError: (state, action) => {
			state.updateDeviceLoading = false;
			state.error = action.payload;
		},

		deleteDevice: (state) => {
			state.deleteDeviceLoading = true;
			state.completeDeleteDevice = false;
		},
		deleteDeviceSuccess: (state) => {
			state.deleteDeviceLoading = false;
			state.completeDeleteDevice = true;
		},
		deleteDeviceError: (state, action) => {
			state.deleteDeviceLoading = false;
			state.completeDeleteDevice = true;
			state.error = action.payload;
		},

		// ✅ Devices Fields Actions
		loadDeviceFields: (state) => {
			state.loadDeviceFieldsLoading = true;
			state.error = null;
		},
		loadDeviceFieldsSuccess: (state, action) => {
			const { orgID, deviceID, fields } = action.payload;
			state.data[orgID] = state.data[orgID]?.map((device) =>
				device.deviceID === deviceID ? { ...device, fields } : device
			);
			state.loadDeviceFieldsLoading = false;
		},
		loadDeviceFieldsError: (state, action) => {
			state.loadDeviceFieldsLoading = false;
			state.error = action.payload;
		},

		createDeviceField: (state) => {
			state.createDeviceFieldLoading = true;
		},
		createDeviceFieldSuccess: (state) => {
			state.createDeviceFieldLoading = false;
		},
		createDeviceFieldError: (state, action) => {
			state.createDeviceFieldLoading = false;
			state.error = action.payload;
		},
		updateDeviceField: (state) => {
			state.updateDeviceFieldLoading = true;
		},
		updateDeviceFieldSuccess: (state) => {
			state.updateDeviceFieldLoading = false;
		},
		updateDeviceFieldError: (state, action) => {
			state.updateDeviceFieldLoading = false;
			state.error = action.payload;
		},

		deleteDeviceField: (state) => {
			state.deleteDeviceFieldLoading = true;
		},
		deleteDeviceFieldSuccess: (state) => {
			state.deleteDeviceFieldLoading = false;
		},
		deleteDeviceFieldError: (state, action) => {
			state.deleteDeviceFieldLoading = false;
			state.error = action.payload;
		},

		// ✅ Devices Location Actions
		loadDeviceLocation: (state) => {
			state.loadDeviceLocationLoading = true;
		},
		loadDeviceLocationSuccess: (state, action) => {
			const { orgID, deviceID, location } = action.payload;
			state.data[orgID] = state.data[orgID]?.map((device) =>
				device.deviceID === deviceID ? { ...device, location } : device
			);
			state.loadDeviceLocationLoading = false;
		},
		loadDeviceLocationError: (state, action) => {
			state.loadDeviceLocationLoading = false;
			state.error = action.payload;
		},
		createDeviceLocation: (state) => {
			state.createDeviceLocationLoading = true;
			state.error = null; // Clear any previous errors
		},
		createDeviceLocationSuccess: (state) => {
			state.createDeviceLocationLoading = false;
		},
		createDeviceLocationError: (state, action) => {
			state.createDeviceLocationLoading = false;
			state.error = action.payload;
		},
		updateDeviceLocation: (state) => {
			state.updateDeviceLocationLoading = true;
			state.error = null; // Clear any previous errors
		},
		updateDeviceLocationSuccess: (state) => {
			state.updateDeviceLocationLoading = false;
		},
		updateDeviceLocationError: (state, action) => {
			state.updateDeviceLocationLoading = false;
			state.error = action.payload;
		},
		deleteDeviceLocation: (state) => {
			state.deleteDeviceLocationLoading = true;
		},
		deleteDeviceLocationSuccess: (state, action) => {
			state.deleteDeviceLocationLoading = false;
			const { deviceID } = action.payload;
			const orgID = Object.keys(state.data).find((key) =>
				state.data[key]?.some((d) => d.deviceID === deviceID)
			);
			if (orgID) {
				state.data[orgID] = state.data[orgID].map((device) =>
					device.deviceID === deviceID ? { ...device, location: null } : device
				);
			}
		},
		deleteDeviceLocationError: (state, action) => {
			state.deleteDeviceLocationLoading = false;
			state.error = action.payload;
		},

		// ✅ Device Sites Actions
		updateDeviceSites: (state) => {
			state.error = null;
		},
		updateDeviceSitesError: (state, action) => {
			state.error = action.payload;
		},

		// ✅ Devices Helpers Actions
		actionDevices: (state) => {
			state.error = null;
			state.actionDevicesLoading = true;
			state.actionDevicesComplete = false;
		},
		actionDevicesSuccess: (state) => {
			state.actionDevicesLoading = false;
			state.actionDevicesComplete = true;
		},
		actionDevicesError: (state, action) => {
			state.actionDevicesLoading = false;
			state.error = action.payload;
			state.actionDevicesComplete = false;
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
	}
});

export const devicesActions = devicesSlice.actions;
export default devicesSlice.reducer;
